import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useGetFilterListDataQuery } from "../../../../store/queries/Verification";
import { bulkUploadShopCertificate } from "../../api";

const UPLOAD_TYPES = [
  { label: "Image", value: "1" },
  { label: "Zip", value: "2" },
];

const useBulkUpload = ({ refetch, handleClose }) => {
  const [fileError, setFileError] = useState("");

  const { data: basicData = {} } = useGetFilterListDataQuery({});
  const itemTypeOptions = basicData?.data?.item_types || [];

  const formik = useFormik({
    initialValues: {
      upload_type: "1",
      folder_name: "",
      certificate_start: "",
      certificate_end: "",
      item_type: "",
      remark: "",
      images: [],
      zipFile: "",
    },
    validationSchema: Yup.object({
      upload_type: Yup.string().required("Upload type is required"),
      certificate_start: Yup.string()
        .trim()
        .required("Certificate start is required"),
      certificate_end: Yup.string()
        .trim()
        .required("Certificate end is required"),
      item_type: Yup.string().required("Item type is required"),
      folder_name: Yup.string()
        .trim()
        .when("upload_type", {
          is: (value) => value === "1",
          then: (schema) => schema.required("Folder name is required"),
        }),
      images: Yup.array().when("upload_type", {
        is: (value) => value == "1",
        then: (schema) => schema.min(1, "Select at least one image"),
        otherwise: () => Yup.array().nullable(),
      }),
      zipFile: Yup.mixed().when("upload_type", {
        is: (value) => value === "2",
        then: (schema) => schema.required("Select a zip file"),
        otherwise: () => Yup.mixed().nullable(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("upload_type", values.upload_type);
      formData.append("certificate_start", values.certificate_start);
      formData.append("certificate_end", values.certificate_end);
      formData.append("item_type", values.item_type);
      if (values.remark) formData.append("remark", values.remark);

      if (values.upload_type === "1") {
        formData.append("folder_name", values.folder_name);
        values.images.forEach((img) => {
          formData.append("images[]", img.file);
        });
      } else {
        formData.append("zip_file", values.zipFile);
      }

      const response = await bulkUploadShopCertificate(formData);
      const payload = response?.data ?? response;
      if (payload?.status === true) {
        toast.success(payload?.msg || "Bulk upload queued successfully");
        handleClose();
        refetch();
      } else {
        toast.error(payload?.msg || "Something went wrong");
      }
      setSubmitting(false);
    },
  });

  const getFieldError = (fieldName) => {
    if ((fieldName === "images" || fieldName === "zipFile") && fileError)
      return fileError;
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const handleUploadTypeChange = (option) => {
    formik.setFieldValue("upload_type", option?.value || "");
    // reset the file fields when switching modes
    formik.setFieldValue("images", []);
    formik.setFieldValue("zipFile", null);
    setFileError("");
  };

  const handleItemTypeChange = (option) => {
    formik.setFieldValue("item_type", option?._id || "");
  };

  const handleImagesChange = (e) => {
    const files = e?.target?.files;
    if (!files?.length) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 10 * 1024 * 1024;
    const maxCount = 200;
    const errors = [];
    const validFiles = [];

    Array.from(files).forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`File ${file.name} is not a valid image type`);
      } else if (file.size > maxSize) {
        errors.push(`File ${file.name} exceeds the maximum size of 10MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setFileError(errors[0]);
      return;
    }

    const readFiles = (filesToRead) =>
      Promise.all(
        filesToRead.map(
          (file) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve({ file, thumb: reader.result });
              };
              reader.readAsDataURL(file);
            }),
        ),
      );

    readFiles(validFiles).then((validFilesWithThumbs) => {
      const existing = formik.values.images || [];
      const combined = [...existing, ...validFilesWithThumbs];
      if (combined.length > maxCount) {
        setFileError(`You can upload a maximum of ${maxCount} images`);
        return;
      }
      formik.setFieldValue("images", combined);
      setFileError("");
    });
  };

  const handleImagesDelete = (data) => {
    const remaining = (formik.values.images || []).filter(
      (item) => item?.thumb !== data?.thumb,
    );
    formik.setFieldValue("images", remaining);
  };

  const handleZipChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const maxSize = 200 * 1024 * 1024;
    const isZip =
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed" ||
      file.name?.toLowerCase().endsWith(".zip");

    if (!isZip) {
      setFileError("Please select a valid .zip file");
      return;
    }
    if (file.size > maxSize) {
      setFileError("Zip file exceeds the maximum size of 200MB");
      return;
    }
    formik.setFieldValue("zipFile", file);
    setFileError("");
  };

  return {
    formik,
    uploadTypeOptions: UPLOAD_TYPES,
    itemTypeOptions,
    getFieldError,
    handleUploadTypeChange,
    handleItemTypeChange,
    handleImagesChange,
    handleImagesDelete,
    handleZipChange,
  };
};

export default useBulkUpload;
